// Story 6.18 (follow-up F7) — the hCaptcha SITE key, and the one rule about
// it that has to hold at build time rather than at runtime.
//
// The site key is public by construction: it ships inside the JS bundle and
// anyone can read it. The half that gates anything is the SECRET, which lives
// only on the server (`server/src/services/captcha_service.rs`). So this
// module is not about keeping anything hidden — it is about the fact that
// **the key is baked at build time**, which means a wrong one cannot be fixed
// with a config change: it is a rebuild and a redeploy, discovered by users
// who cannot sign up.
//
// Two wrong keys are specifically worth failing a build over:
//   * the PLACEHOLDER the production pipeline ships until the owner creates
//     the hCaptcha account (`OWNER-DECISION-REQUIRED-hcaptcha-site-key`) —
//     the widget cannot render for it, so with the server gate armed EVERY
//     production signup fails on cutover day; and
//   * hCaptcha's published TEST keys, which always pass. Pairing a real
//     secret with a test site key produces a gate that verifies a token
//     anybody can mint — on, looking on, checking nothing.

/// hCaptcha's published always-passing test site key. Deliberately the local
/// fallback: dev, the vitest suite and any deployment whose server gate is
/// off all want a widget that never shows a challenge.
export const HCAPTCHA_TEST_SITE_KEY = "10000000-ffff-ffff-ffff-000000000001";

/// The placeholder the production pipeline carries until step 3 of the
/// story's owner actions is done.
const SITE_KEY_PLACEHOLDER_MARKER = "OWNER-DECISION-REQUIRED";

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/// hCaptcha's test keys are a family, not one value: `10000000-…-0001`
/// (always pass), `20000000-…-0002` (always challenge), `30000000-…-0003`
/// (pass, no UI), and `apps/web/scripts/docker-build.sh` uses yet another.
/// Matching the FAMILY is what stops the next one slipping through.
const HCAPTCHA_TEST_KEY_SHAPE = /^[0-9]0000000-ffff-ffff-ffff-[0-9a-f]{12}$/i;

export function isHcaptchaTestSiteKey(key: string): boolean {
  return HCAPTCHA_TEST_KEY_SHAPE.test(key.trim());
}

/// The runtime resolution: the configured key, or the always-passing test key
/// when nothing is configured. Never throws — a dev server must start.
export function resolveHcaptchaSiteKey(configured: string | undefined): string {
  return configured?.trim() || HCAPTCHA_TEST_SITE_KEY;
}

/// The build-time rule, called from `vite.config.ts` when — and only when —
/// the build target is production. Throws with an actionable message, which
/// is the entire point: a failed build is cheap, a production deploy whose
/// signup is broken is not.
export function assertProductionHcaptchaSiteKey(
  configured: string | undefined,
): void {
  const key = configured?.trim() ?? "";
  const refuse = (why: string): never => {
    throw new Error(
      `REACT_APP_HCAPTCHA_SITE_KEY is ${why}, and this is a production build.\n` +
        "Create the site at https://dashboard.hcaptcha.com/, then set the " +
        "`hcaptchaSiteKey` variable in azure-pipelines-web-deploy.yml to its " +
        "site key (and put the PAIRED secret in Key Vault as `hcaptcha-secret`).\n" +
        "See _bmad-output/implementation-artifacts/6-18-register-bot-gate.md " +
        "§'Owner actions before production'.",
    );
  };

  if (key === "") {
    refuse("missing or empty");
  }
  if (key.toUpperCase().includes(SITE_KEY_PLACEHOLDER_MARKER)) {
    refuse(`still the placeholder (${key})`);
  }
  if (isHcaptchaTestSiteKey(key)) {
    refuse(`one of hCaptcha's always-passing TEST keys (${key})`);
  }
  if (!UUID_SHAPE.test(key)) {
    refuse(`not a valid hCaptcha site key (${key}) — it must be a UUID`);
  }
}
